export const DEFAULT_TIME_ZONE = "Europe/Moscow";

export function safeTimeZone(value) {
  const candidate = value || DEFAULT_TIME_ZONE;
  try {
    new Intl.DateTimeFormat("ru-RU", { timeZone: candidate }).format();
    return candidate;
  } catch {
    return DEFAULT_TIME_ZONE;
  }
}

export function formatDateTime(value, options = {}) {
  if (!value) return options.fallback || "без срока";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return options.fallback || "без срока";
  return new Intl.DateTimeFormat(options.locale || "ru-RU", {
    timeZone: safeTimeZone(options.timeZone),
    day: options.day || "2-digit",
    month: options.month || "short",
    ...(options.year ? { year: options.year } : {}),
    ...(options.weekday ? { weekday: options.weekday } : {}),
    ...(options.withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  }).format(date);
}

function zonedParts(value, timeZone) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: safeTimeZone(timeZone),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(value));
  return Object.fromEntries(parts.filter((part) => part.type !== "literal").map((part) => [part.type, part.value]));
}

export function toZonedDateTimeInput(value, timeZone = DEFAULT_TIME_ZONE) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const parts = zonedParts(date, timeZone);
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

export function toZonedDateInput(value = new Date(), timeZone = DEFAULT_TIME_ZONE) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const parts = zonedParts(date, timeZone);
  return `${parts.year}-${parts.month}-${parts.day}`;
}

export function zonedDateTimeInputToIso(value, timeZone = DEFAULT_TIME_ZONE) {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value || "");
  if (!match) return null;
  const [, year, month, day, hour, minute] = match;
  const wallClockUtc = Date.UTC(+year, +month - 1, +day, +hour, +minute, 0);
  let candidate = wallClockUtc;
  for (let pass = 0; pass < 2; pass += 1) {
    const parts = zonedParts(candidate, timeZone);
    const renderedAsUtc = Date.UTC(
      +parts.year,
      +parts.month - 1,
      +parts.day,
      +parts.hour,
      +parts.minute,
      +parts.second,
    );
    candidate -= renderedAsUtc - wallClockUtc;
  }
  return new Date(candidate).toISOString();
}

export function timeZoneBadge(timeZone = DEFAULT_TIME_ZONE) {
  const zone = safeTimeZone(timeZone);
  const offset = new Intl.DateTimeFormat("en", {
    timeZone: zone,
    timeZoneName: "shortOffset",
  }).formatToParts(new Date()).find((part) => part.type === "timeZoneName")?.value || zone;
  return `${zone.split("/").at(-1)?.replaceAll("_", " ") || zone} / ${offset.replace("GMT", "UTC")}`;
}
